/**
 * My preferences helpers (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-F).
 */

export interface SavedAlertView { name: string; query: string }

export interface MeProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  timezone: string | null;
  slack_handle: string | null;
  slack_dm_connected: boolean;
  support_from_email: string | null;
  support_from_display_name: string | null;
  support_signature_html: string | null;
  muted_events: string[];
  saved_alert_views: SavedAlertView[];
  events: { key: string; label: string; required: boolean }[];
}

export const EDITABLE_FIELDS = [
  'full_name', 'timezone', 'slack_handle',
  'support_from_email', 'support_from_display_name', 'support_signature_html',
] as const;
type Editable = typeof EDITABLE_FIELDS[number];

/** Only the fields the admin actually changed (so a save never rewrites the rest). */
export function profileDiff(original: MeProfile, draft: Partial<MeProfile>): Partial<Record<Editable, string>> {
  const out: Partial<Record<Editable, string>> = {};
  EDITABLE_FIELDS.forEach(k => {
    if (!(k in draft)) return;
    const before = (original[k] ?? '') as string;
    const after = ((draft[k] as string | null | undefined) ?? '').toString();
    if (after.trim() !== before.trim()) out[k] = after;
  });
  return out;
}

const FALLBACK_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto',
  'America/Vancouver', 'America/Halifax', 'America/St_Johns', 'Europe/London', 'Europe/Paris', 'UTC',
];

/** Every IANA zone the browser knows, else a short common list. */
export function timezoneOptions(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    const zones = intl.supportedValuesOf?.('timeZone');
    if (zones && zones.length) return zones;
  } catch {
    /* older engines */
  }
  return FALLBACK_ZONES;
}

/** Name for "Save view" on the Alerts page: trimmed, unique, ≤60 chars. */
export function viewNameError(name: string, existing: SavedAlertView[]): string | null {
  const n = name.trim();
  if (!n) return 'Give the view a name.';
  if (n.length > 60) return 'Keep the name under 60 characters.';
  if (existing.some(v => v.name.toLowerCase() === n.toLowerCase())) return 'You already have a view with that name.';
  return null;
}
